import { __decorate, __metadata, __awaiter } from 'tslib';
import { StateManager } from '../store/state-manager.js';
import { BuiltinUiState } from '../store/builtin-state.js';
import { Service } from '../../decorators/class/service.js';
import { generateRandomId } from '../../utils/string-utils.js';
import { ReplaySubject, BehaviorSubject } from 'rxjs';
import { InjectorService } from '@tencent/ioc';
import { throttleTime, scan, first } from 'rxjs/operators';
import { $logger } from '../../controllers/logger/logger.js';
import { isServer } from '../../utils/env.js';
import { WorkbenchConfigurationState } from '../settings/auto-sync-settings.js';
import { isHorizontalSurfaceModeOption, isVerticalSurfaceModeOption, isStackSurfaceModeOption, isFloatSurfaceModeOption, isVirtualSurfaceModeOption } from './surface-type.js';
import { GridService, DEFAULT_ROOT_GRID, DEFAULT_FLOAT_GRID } from './grid-service.js';

function isSurfaceConfig(object) {
    return typeof object.id === 'string' && typeof object.grid === 'string' && object.stream !== undefined;
}
const defaultFloatStyle = {
    position: 'absolute',
    boxShadow: '0 0 4px #999',
    top: 160,
    left: 0,
    right: 0,
    margin: '0 auto',
    width: 600,
    height: 340,
};
/**
 * 用于管理多编辑器状态的服务
 *
 * @public
 */
let SurfaceService = class SurfaceService {
    constructor(store, gridService) {
        var _a, _b;
        this.store = store;
        this.gridService = gridService;
        this.info = {
            current: null,
            focused: false,
        };
        this.config = new Map();
        // this.grids = new Map();
        this.editorInfos = new Map();
        this.editorMap = new WeakMap();
        this.initializeSurfaceInDefaultGrid();
        store.register(BuiltinUiState.FocusState, this.info);
        store.register(BuiltinUiState.ModelState, { editor: null });
        store.register(BuiltinUiState.ModelTempState, { editor: null });
        store.register(BuiltinUiState.SelectionState, { editor: null });
        store.register(BuiltinUiState.SelectionTempState, { editor: null });
        const throttleConfig = { leading: false, trailing: true };
        const modelThrottleTime = (_a = store.getValue(WorkbenchConfigurationState.ModelThrottleTime)) !== null && _a !== void 0 ? _a : 150;
        store.get(BuiltinUiState.ModelTempState)
            .pipe(throttleTime(modelThrottleTime, undefined, throttleConfig))
            .subscribe((payload) => {
            store.update(BuiltinUiState.ModelState, payload);
        });
        const selectionThrottleTime = (_b = store.getValue(WorkbenchConfigurationState.SelectionThrottleTime)) !== null && _b !== void 0 ? _b : 150;
        store.get(BuiltinUiState.SelectionTempState)
            .pipe(throttleTime(selectionThrottleTime, undefined, throttleConfig))
            .subscribe((payload) => {
            store.update(BuiltinUiState.SelectionState, payload);
        });
        store.get(BuiltinUiState.FocusState)
            .pipe(scan((acc, curr) => (Object.assign(Object.assign({}, acc), curr)), {}))
            .subscribe((focusState) => {
            this.info = focusState;
        });
    }
    initializeSurfaceInDefaultGrid() {
        this.gridService.initializeDefaultGrid();
        this.schema = this.compose(this.gridService.getGrid(DEFAULT_ROOT_GRID));
        this.float = this.compose(this.gridService.getGrid(DEFAULT_FLOAT_GRID));
        this.store.register(BuiltinUiState.FloatSurfaceStyle, defaultFloatStyle);
        this.store.register(BuiltinUiState.Surface, this.schema);
        this.store.register(BuiltinUiState.FloatSurface, this.float);
    }
    compose(parent) {
        var _a, _b;
        const gridEntity = {
            id: parent.id,
            type: parent.type,
            grids: (_a = parent.grids) === null || _a === void 0 ? void 0 : _a.filter((gridId) => {
                if (this.gridService.hasGrid(gridId)) {
                    return true;
                }
                return false;
            }).map(gridId => this.compose(this.gridService.getGrid(gridId))),
            surfaces: (_b = parent.surfaces) === null || _b === void 0 ? void 0 : _b.map(surface => this.get(surface)).filter(surface => !!surface),
            current: parent.current,
            stackCurrent: parent.stackCurrent,
        };
        return gridEntity;
    }
    /**
     * 获取 Surface 信息
     *
     * @param surfaceId -
     */
    get(surfaceId) {
        var _a;
        return (_a = this.config.get(surfaceId)) !== null && _a !== void 0 ? _a : null;
    }
    /**
     * 获取 Grid 信息
     *
     * @param gridId -
     */
    getGrid(gridId) {
        return this.gridService.getGrid(gridId);
    }
    update() {
        this.schema = this.compose(this.gridService.getGrid(DEFAULT_ROOT_GRID));
        this.store.get(BuiltinUiState.Surface).next(this.schema);
        this.float = this.compose(this.gridService.getGrid(DEFAULT_FLOAT_GRID));
        this.store.get(BuiltinUiState.FloatSurface).next(this.float);
    }
    /**
     * 告知工作台已聚焦到某个 surface 上
     *
     * @param id - Surface ID
     */
    onFocus(id) {
        const surface = this.get(id);
        const focusState = this.store.getValue(BuiltinUiState.FocusState);
        if ((focusState === null || focusState === void 0 ? void 0 : focusState.current) === surface && focusState.focused) {
            return;
        }
        this.store.update(BuiltinUiState.FocusState, {
            focused: true,
            current: surface,
        });
        this.forceUpdate();
    }
    /**
     *  获取当前聚焦编辑器
     * @public
     */
    getCurrent() {
        var _a;
        const state = this.store.getValue(BuiltinUiState.FocusState);
        if (state === null || state === void 0 ? void 0 : state.focused) {
            return (_a = state.current) !== null && _a !== void 0 ? _a : undefined;
        }
        return undefined;
    }
    /**
     * 设置一个 Grid 下活跃的 Surface
     *
     * @param gridId -
     * @param surfaceId -
     */
    setCurrent(surfaceId) {
        return new Promise((resolve) => {
            var _a, _b;
            const gridId = (_a = this.get(surfaceId)) === null || _a === void 0 ? void 0 : _a.grid;
            if (!gridId) {
                $logger.warn('gridId not exist');
                return resolve(undefined);
            }
            const grid = this.gridService.getGrid(gridId);
            if (!grid) {
                $logger.warn('grid not exist');
                return resolve(undefined);
            }
            if (grid.current && grid.current !== surfaceId) {
                const editor = this.getEditor(grid.current);
                const adapter = this.getAdapter(grid.current);
                adapter === null || adapter === void 0 ? void 0 : adapter.requestBlur(editor);
            }
            grid.current = surfaceId;
            this.traverseVisibility(surfaceId);
            this.onFocus(surfaceId);
            (_b = this.config.get(surfaceId)) === null || _b === void 0 ? void 0 : _b.stream.pipe(first()).subscribe(() => {
                const editor = this.getEditor(surfaceId);
                const adapter = this.getAdapter(surfaceId);
                adapter === null || adapter === void 0 ? void 0 : adapter.requestFocus(editor);
                resolve(surfaceId);
            });
            this.update();
        });
    }
    traverseVisibility(surfaceId) {
        var _a;
        let id = surfaceId;
        let surfaceConfig;
        const gridInfo = this.getGridOfSurface(surfaceId);
        (_a = gridInfo === null || gridInfo === void 0 ? void 0 : gridInfo.surfaces) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
            const config = this.config.get(item);
            if (config) {
                config.visible = false;
            }
        });
        do {
            surfaceConfig = this.config.get(id);
            if (!surfaceConfig) {
                continue;
            }
            this.changeSurfaceVisibility(true, surfaceConfig);
            id = surfaceConfig.virtualParent;
        } while (id);
        this.updateStackCurrentForStackGrid(surfaceId);
    }
    changeSurfaceVisibility(visible, surfaceConfig) {
        var _a;
        surfaceConfig.visible = visible;
        (_a = surfaceConfig.virtualSurfaces) === null || _a === void 0 ? void 0 : _a.forEach((surfaceId) => {
            const config = this.config.get(surfaceId);
            if (!config) {
                return;
            }
            this.changeSurfaceVisibility(visible, config);
        });
    }
    updateStackCurrentForStackGrid(surfaceId) {
        let gridInfo = this.getGridOfSurface(surfaceId);
        let gridId = gridInfo === null || gridInfo === void 0 ? void 0 : gridInfo.id;
        while (gridInfo === null || gridInfo === void 0 ? void 0 : gridInfo.parent) {
            gridId = gridInfo.id;
            gridInfo = this.gridService.getGrid(gridInfo === null || gridInfo === void 0 ? void 0 : gridInfo.parent);
            if (!gridInfo) {
                return;
            }
            gridInfo.current = surfaceId;
            if (gridInfo.type === 'stack') {
                gridInfo.stackCurrent = gridId;
            }
        }
    }
    /**
     * 告知工作台已从某个 surface 上失焦
     *
     * @param id - SurfaceId
     */
    onBlur(id) {
        var _a;
        let surface;
        if (id !== ((_a = this.info.current) === null || _a === void 0 ? void 0 : _a.id))
            return;
        if (id) {
            surface = this.get(id);
        }
        else {
            surface = this.info.current;
        }
        this.store.update(BuiltinUiState.FocusState, {
            focused: false,
            current: surface,
        });
    }
    newGridId() {
        return `grid-${generateRandomId()}`;
    }
    newSurfaceId() {
        return `surface-${generateRandomId()}`;
    }
    createSync(...args) {
        let surfaceConfig = undefined;
        let ssrChildren;
        let openEditorOptions = { gridType: 'horizontal' };
        if (args.length > 0 && typeof args[0] === 'string') {
            // 兼容旧参数createSync(mode?: GridType, target?: GridId, floatStyle?: CSSProperties, ssrChildren?: string)
            const [mode = 'horizontal', target, floatStyle, ssrString] = args;
            ssrChildren = ssrString;
            openEditorOptions = this.getModeOptions(mode, target, floatStyle);
        }
        else if (args.length > 0 && typeof args[0] === 'object') {
            [openEditorOptions, ssrChildren] = args;
        }
        if (openEditorOptions
            && (isHorizontalSurfaceModeOption(openEditorOptions) || isVerticalSurfaceModeOption(openEditorOptions))) {
            surfaceConfig = this.createGridSync(openEditorOptions, ssrChildren);
        }
        else if (openEditorOptions && isStackSurfaceModeOption(openEditorOptions)) {
            surfaceConfig = this.createStackSync(openEditorOptions, ssrChildren);
        }
        else if (openEditorOptions && isFloatSurfaceModeOption(openEditorOptions)) {
            surfaceConfig = this.createFloatSync(openEditorOptions, ssrChildren);
        }
        else if (openEditorOptions && isVirtualSurfaceModeOption(openEditorOptions)) {
            const options = openEditorOptions;
            surfaceConfig = this.createVirtualSync(options, ssrChildren);
        }
        if (!surfaceConfig) {
            return undefined;
        }
        this.traverseVisibility(surfaceConfig.id);
        this.update();
        return surfaceConfig;
    }
    getModeOptions(mode, target, floatStyle) {
        var _a, _b;
        let surfaceId;
        if (target) {
            surfaceId = (_b = (_a = this.gridService.getGrid(target)) === null || _a === void 0 ? void 0 : _a.surfaces) === null || _b === void 0 ? void 0 : _b[0];
        }
        if (mode === 'horizontal' || mode === 'vertical') {
            const option = {
                gridType: mode,
                target: surfaceId,
            };
            return option;
        }
        if (mode === 'float') {
            const options = {
                gridType: 'float',
                isNewFloat: false,
                floatStyle,
            };
            return options;
        }
        if (mode === 'stack') {
            const options = {
                gridType: 'stack',
                target: surfaceId,
            };
            return options;
        }
        return undefined;
    }
    create(...args) {
        return __awaiter(this, void 0, void 0, function* () {
            const surfaceConfig = this.createSync(...args);
            if (!surfaceConfig) {
                return Promise.reject();
            }
            if (isServer) {
                return Promise.resolve(Object.assign({}, surfaceConfig));
            }
            yield this.waitForSurfaceRender(surfaceConfig);
            return Promise.resolve(surfaceConfig);
        });
    }
    /**
     * 等待surface渲染完成
     * @param id - SurfaceId或SurfaceConfig
     */
    waitForSurfaceRender(surfaceConfigOrId) {
        return __awaiter(this, void 0, void 0, function* () {
            const config = isSurfaceConfig(surfaceConfigOrId) ? surfaceConfigOrId : this.get(surfaceConfigOrId);
            if (config === null) {
                return Promise.reject('surface id not exist');
            }
            const dom = yield new Promise(resolve => config === null || config === void 0 ? void 0 : config.stream.pipe(first()).subscribe((ref) => {
                resolve(ref);
            }));
            config.dom = dom;
            return config;
        });
    }
    getGridOfSurface(target) {
        const surface = this.config.get(target);
        if (!surface) {
            return undefined;
        }
        return this.gridService.getGrid(surface.grid);
    }
    newSurfaceConfig(target, ssrChildren) {
        const surfaceId = this.newSurfaceId();
        const gridId = target !== null && target !== void 0 ? target : this.newGridId();
        const subject = new ReplaySubject();
        const focusStream = new BehaviorSubject(false);
        const surfaceConfig = {
            id: surfaceId,
            grid: gridId,
            stream: subject,
            ssrChildren,
            focusStream,
            visible: false,
        };
        return surfaceConfig;
    }
    createGridSync(option, ssrChildren) {
        let gridId = DEFAULT_ROOT_GRID;
        if (option.target) {
            const grid = this.getGridOfSurface(option.target);
            if (!grid) {
                $logger.error(`Target grid ${option.target} does not exist.`);
                return;
            }
            gridId = grid.id;
        }
        const targetGrid = this.gridService.hasGrid(gridId);
        if (!targetGrid) {
            $logger.error(`Target grid ${gridId} does not exist.`);
            return;
        }
        const surfaceConfig = this.newSurfaceConfig(gridId, ssrChildren);
        this.gridService.addToGrid(gridId, surfaceConfig, option.gridType);
        this.config.set(surfaceConfig.id, surfaceConfig);
        return surfaceConfig;
    }
    createStackSync(option, ssrChildren) {
        var _a, _b, _c, _d;
        const grid = this.getGridOfSurface((_c = (_a = option.target) !== null && _a !== void 0 ? _a : (_b = this.info.current) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : DEFAULT_ROOT_GRID);
        if (option.target && !grid) {
            $logger.error(`Target grid ${option.target} does not exist.`);
            return;
        }
        const gridId = (_d = grid === null || grid === void 0 ? void 0 : grid.id) !== null && _d !== void 0 ? _d : DEFAULT_ROOT_GRID;
        const surfaceConfig = this.newSurfaceConfig(gridId, ssrChildren);
        this.gridService.addToGrid(gridId, surfaceConfig);
        this.config.set(surfaceConfig.id, surfaceConfig);
        return surfaceConfig;
    }
    createFloatSync(option, ssrChildren) {
        var _a;
        const surfaceConfig = this.newSurfaceConfig(DEFAULT_FLOAT_GRID, ssrChildren);
        this.gridService.addToFloatGrid(surfaceConfig, option.isNewFloat);
        this.config.set(surfaceConfig.id, surfaceConfig);
        if (surfaceConfig.grid === DEFAULT_FLOAT_GRID) {
            this.floatStyle = (_a = option.floatStyle) !== null && _a !== void 0 ? _a : {};
        }
        return surfaceConfig;
    }
    createVirtualSync(virtualOption, ssrChildren) {
        var _a, _b;
        let gridId = DEFAULT_ROOT_GRID;
        let targetSurface;
        if (virtualOption.target) {
            targetSurface = this.config.get(virtualOption.target);
            gridId = (_a = targetSurface === null || targetSurface === void 0 ? void 0 : targetSurface.grid) !== null && _a !== void 0 ? _a : DEFAULT_ROOT_GRID;
        }
        const surfaceConfig = this.newSurfaceConfig(gridId, ssrChildren);
        this.gridService.addToGrid(gridId, surfaceConfig);
        surfaceConfig.targetDom = virtualOption.targetDom;
        surfaceConfig.virtualParent = targetSurface === null || targetSurface === void 0 ? void 0 : targetSurface.id;
        if (targetSurface && !(targetSurface === null || targetSurface === void 0 ? void 0 : targetSurface.virtualSurfaces)) {
            targetSurface.virtualSurfaces = [];
        }
        (_b = targetSurface === null || targetSurface === void 0 ? void 0 : targetSurface.virtualSurfaces) === null || _b === void 0 ? void 0 : _b.push(surfaceConfig.id);
        this.config.set(surfaceConfig.id, surfaceConfig);
        return surfaceConfig;
    }
    set floatStyle(style) {
        this.store.update(BuiltinUiState.FloatSurfaceStyle, Object.assign(Object.assign({}, defaultFloatStyle), style));
    }
    /**
     * 销毁一个 Surface并刷新，同时其挂载的编辑器会被关闭
     *
     * @param id - SurfaceId
     * @returns
     */
    close(id) {
        this.closeSurface(id);
        this.update();
        this.forceUpdate();
    }
    closeSurface(id) {
        var _a, _b, _c;
        const surface = this.get(id);
        if (!surface)
            return;
        const grid = this.gridService.getGrid(surface.grid);
        if (!(grid === null || grid === void 0 ? void 0 : grid.surfaces))
            return;
        if (surface.virtualParent) {
            const virtualSurfaceParent = this.config.get(surface.virtualParent);
            const index = (_b = (_a = virtualSurfaceParent === null || virtualSurfaceParent === void 0 ? void 0 : virtualSurfaceParent.virtualSurfaces) === null || _a === void 0 ? void 0 : _a.indexOf(id)) !== null && _b !== void 0 ? _b : -1;
            if (index > 0) {
                (_c = virtualSurfaceParent === null || virtualSurfaceParent === void 0 ? void 0 : virtualSurfaceParent.virtualSurfaces) === null || _c === void 0 ? void 0 : _c.splice(index, 1);
            }
        }
        const idx = grid.surfaces.indexOf(surface.id);
        if (idx >= 0) {
            grid.surfaces.splice(idx, 1);
            if (grid.surfaces.length > 0) {
                grid.current = grid.surfaces[Math.max(0, idx - 1)];
                grid.stackCurrent = grid.current;
                if (surface.visible) {
                    this.traverseVisibility(grid.current);
                }
            }
            else {
                this.gridService.deleteGrid(surface.grid);
            }
            this.closeAllChildSurface(surface);
            this.destroyEditor(id);
            this.config.delete(id);
        }
    }
    closeAllChildSurface(closedSurface) {
        const grid = this.gridService.getGrid(closedSurface.grid);
        if (!(grid === null || grid === void 0 ? void 0 : grid.surfaces))
            return;
        grid.surfaces.forEach((surfaceId) => {
            const surface = this.get(surfaceId);
            if ((surface === null || surface === void 0 ? void 0 : surface.virtualParent) === closedSurface.id) {
                this.closeSurface(surface.id);
            }
        });
    }
    destroyEditor(id) {
        const editorInfo = this.editorInfos.get(id);
        if (!editorInfo)
            return;
        this.onBlur(id);
        editorInfo.adapter.destroy(editorInfo.editor);
        this.editorMap.delete(editorInfo.editor);
        this.editorInfos.delete(id);
    }
    /**
     * 向一个 Surface 部署一个编辑器
     *
     * @param id - SurfaceId
     * @param editor - 编辑器实例
     * @param adapter - 编辑器对应的适配器
     */
    addEditor(id, editor, adapter) {
        this.destroyEditor(id);
        this.editorInfos.set(id, { editor, adapter });
        if (typeof editor === 'object' && editor !== null) {
            this.editorMap.set(editor, id);
        }
    }
    /**
     * 获取对应 SurfaceId 下的 Editor
     *
     * @param id - SurfaceId
     */
    getEditor(id) {
        var _a, _b;
        if (id) {
            return (_b = (_a = this.editorInfos.get(id)) === null || _a === void 0 ? void 0 : _a.editor) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    /**
     * 获取对应 SurfaceId 下的 Adapter
     *
     * @param id - SurfaceId
     */
    getAdapter(id) {
        var _a, _b;
        if (id) {
            return (_b = (_a = this.editorInfos.get(id)) === null || _a === void 0 ? void 0 : _a.adapter) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    getSurfaceId(editor) {
        return this.editorMap.get(editor);
    }
    /**
     * 所有编辑器列表
     *
     */
    get editors() {
        return [...this.editorInfos.values()];
    }
    forceUpdate() {
        InjectorService.emitOn('$forceUpdate', provider => provider.type === 'service');
    }
};
SurfaceService = __decorate([
    Service({ overridable: true }),
    __metadata("design:paramtypes", [StateManager, GridService])
], SurfaceService);

export { SurfaceService, isSurfaceConfig };
//# sourceMappingURL=surface-service.js.map
